import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { setActiveCompanyScope } from '../lib/activeCompany';

export interface Company {
  id: string;
  company_name: string;
  company_type: 'holding' | 'subsidiary' | string;
  is_active: boolean;
}

export const CONSOLIDATED_ID = '__consolidated__';

interface CompanyContextType {
  companies: Company[];
  activeCompany: Company | null;
  setActiveCompany: (company: Company | null) => void;
  loadingCompanies: boolean;
  isHoldingView: boolean;
  isConsolidatedView: boolean;
  activeCompanyId: string | null;
  /** Returns a guaranteed non-null company ID for inserts into tables with NOT NULL company_id.
   *  Falls back to the user's first assigned company when in consolidated view. */
  getRequiredCompanyId: () => string;
}

const CompanyContext = createContext<CompanyContextType | null>(null);

const STORAGE_KEY = 'erp_active_company_id';

export function useCompany() {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error('useCompany must be used within CompanyProvider');
  return ctx;
}

/**
 * P0-COMPANY-SCOPE-1 — the single canonical active-company resolver.
 *
 * Precedence, deliberately with no arbitrary tie-break:
 *   1. an explicit prior selection, if the user is still authorized on it
 *   2. the sole authorized company, when there is exactly one
 *   3. nothing — the user must choose
 *
 * Anything else silently picks a tenant on the user's behalf, which is what
 * `data[0]` did: it handed whichever company sorted first to every create form.
 */
export function resolveDefaultCompany<T extends { id: string }>(
  authorized: T[],
  savedId: string | null,
): T | null {
  const saved = savedId ? authorized.find(c => c.id === savedId) ?? null : null;
  if (saved) return saved;
  return authorized.length === 1 ? authorized[0] : null;
}

/**
 * The company_id a create form may use. Returns '' when the tenant is ambiguous so
 * the caller fails closed — an ambiguous tenant is a question for the user, not a
 * default to guess.
 */
export function resolveRequiredCompanyId<T extends { id: string }>(
  activeCompanyId: string | null,
  authorized: T[],
): string {
  if (activeCompanyId) return activeCompanyId;
  return authorized.length === 1 ? authorized[0].id : '';
}

export function CompanyProvider({ children }: { children: ReactNode }) {
  const { user, profile } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [activeCompany, setActiveCompanyState] = useState<Company | null>(null);
  const [isConsolidated, setIsConsolidated] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(true);

  useEffect(() => {
    if (!user) {
      setCompanies([]);
      setActiveCompanyState(null);
      setIsConsolidated(false);
      setLoadingCompanies(false);
      return;
    }

    (async () => {
      setLoadingCompanies(true);
      const ids = await supabase.rpc('get_user_company_ids');
      if (ids.error || !ids.data?.length) {
        setLoadingCompanies(false);
        return;
      }

      const { data, error } = await supabase
        .from('companies')
        .select('id, company_name, name, legal_name, company_type, is_active')
        .in('id', ids.data)
        .eq('is_active', true)
        .order('company_type', { ascending: false })
        .order('company_name');

      if (error || !data) {
        setLoadingCompanies(false);
        return;
      }

      // companies.company_name is NOT NULL DEFAULT '' — rows created with only `name`
      // (or `legal_name`) carry a blank company_name, which rendered as empty options in
      // every company dropdown and an empty switcher label. Resolve a display name once,
      // here, so every consumer of `companies` / `activeCompany` gets a usable one.
      const named = (data as (Company & { name?: string | null; legal_name?: string | null })[]).map(
        ({ name, legal_name, ...c }) => ({
          ...c,
          company_name: c.company_name?.trim() || name?.trim() || legal_name?.trim() || c.id.slice(0, 8),
        }),
      ) as Company[];
      setCompanies(named);

      const savedId = localStorage.getItem(STORAGE_KEY);
      // Only restore consolidated view from localStorage if the user's role
      // is actually authorized for it. Previously any user could set
      // `erp_active_company_id = '__consolidated__'` in localStorage and
      // trigger the consolidated code paths. The setter already checks this;
      // the loader did not. Fall through to single-company default for any
      // unauthorized restore attempt.
      if (savedId === CONSOLIDATED_ID && isConsolidatedAuthorized()) {
        setIsConsolidated(true);
        setActiveCompanyState(null);
      } else {
        if (savedId === CONSOLIDATED_ID) {
          // Clean the tampered / stale value so a later promotion doesn't silently re-activate it.
          localStorage.removeItem(STORAGE_KEY);
        }
        const saved = savedId && savedId !== CONSOLIDATED_ID
          ? named.find(c => c.id === savedId)
          : null;
        // P0-COMPANY-SCOPE-1 — the default was `data[0]`, i.e. whichever company
        // sorted first (company_type desc, company_name). For a user authorized on
        // one company that is right by accident; for a multi-company user it silently
        // picked a company they never chose — "Special Offices Company" sorts ahead of
        // "Unified Business Factory" — and that company was then handed to New Customer
        // and every other create form as the owning tenant.
        //
        // No wrong-company row ever reached Production from this (audited: 0), because
        // RLS WITH CHECK and the party RPC both refuse a company the caller is not a
        // member of. But a multi-company user WOULD have written to the wrong company
        // successfully, since they are a legitimate member of both. The guard rails
        // that saved us were the ones for a different threat.
        //
        // Precedence now: explicit prior selection -> sole authorized company ->
        // nothing. Never an arbitrary pick.
        const defaultCompany = resolveDefaultCompany(named, saved ? saved.id : savedId);
        setActiveCompanyState(defaultCompany);
        setIsConsolidated(false);
      }
      setLoadingCompanies(false);
    })();
  }, [user]);

  // Roles allowed to use consolidated (all-companies) mode.
  // Entire HR role family (hr_manager + hr_staff + chro) is included so
  // group-level HR operations (cross-subsidiary payroll, attendance,
  // EOSB, expiry alerts, etc.) work from any HR user's login. Must stay
  // in sync with the server-side get_user_company_ids() RPC role list.
  const CONSOLIDATED_ROLES = [
    'group_ceo', 'ceo_commercial', 'ceo_manufacturing', 'coo',
    'chro', 'cfo', 'cbo', 'it_admin', 'super_admin', 'internal_auditor',
    'compliance_officer', 'hr_manager', 'hr_staff',
  ];

  const isConsolidatedAuthorized = (): boolean => {
    const r = profile?.role;
    return !!r && CONSOLIDATED_ROLES.includes(r);
  };

  const setActiveCompany = (company: Company | null) => {
    if (company === null) {
      // Restrict consolidated mode to executive + admin roles
      const userRole = profile?.role;
      if (userRole && !CONSOLIDATED_ROLES.includes(userRole)) {
        console.warn(`[CompanyContext] Role "${userRole}" not authorized for consolidated view. Ignoring.`);
        return; // Silently ignore — don't switch to consolidated
      }
      setIsConsolidated(true);
      setActiveCompanyState(null);
      localStorage.setItem(STORAGE_KEY, CONSOLIDATED_ID);
    } else {
      setIsConsolidated(false);
      setActiveCompanyState(company);
      localStorage.setItem(STORAGE_KEY, company.id);
    }
  };

  const isHoldingView = activeCompany?.company_type === 'holding';
  const isConsolidatedView = isConsolidated;
  const activeCompanyId = isConsolidated ? null : (activeCompany?.id ?? null);

  // Publish the active company to the query-layer scope guard so reference
  // getters fall back to it when a caller forgets to pass companyId.
  useEffect(() => {
    setActiveCompanyScope(activeCompanyId);
  }, [activeCompanyId]);

  // P0-COMPANY-SCOPE-1 — this used to fall back to `companies[0]` whenever no company
  // was active, which is the same arbitrary pick as the loader's old default and more
  // dangerous: 22 call sites feed its result straight into a create payload's
  // company_id. A multi-company user with no explicit selection would have silently
  // written into whichever company sorted first.
  //
  // It now derives a company only when there is exactly one it could possibly be, and
  // otherwise returns '' so the caller fails closed. Callers already treat '' as
  // missing and block submission, which is the correct outcome: an ambiguous tenant is
  // not a default, it is a question for the user.
  const getRequiredCompanyId = (): string => resolveRequiredCompanyId(activeCompanyId, companies);

  return (
    <CompanyContext.Provider value={{
      companies,
      activeCompany,
      setActiveCompany,
      loadingCompanies,
      isHoldingView,
      isConsolidatedView,
      activeCompanyId,
      getRequiredCompanyId,
    }}>
      {children}
    </CompanyContext.Provider>
  );
}
